import { useState } from 'react';
import { motion } from 'framer-motion';
import Lightbox from './Lightbox';
import type { PortfolioImage } from '@/data/portfolio-data';

interface MasonryGalleryProps {
  images: PortfolioImage[];
  pageSize?: number;
}

const MasonryGallery = ({ images, pageSize = 24 }: MasonryGalleryProps) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const visibleImages = images.slice(0, visibleCount);
  const hasMore = visibleCount < images.length;

  return (
    <>
      {/* Masonry via CSS columns: simple, performant, no JS layout */}
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
        {visibleImages.map((img, i) => (
          <motion.button
            key={img.src}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: Math.min(i * 0.015, 0.4) }}
            onClick={() => setLightboxIndex(i)}
            className="group relative mb-4 block w-full overflow-hidden rounded-xl border border-border bg-card break-inside-avoid focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={`Agrandir : ${img.alt}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              decoding="async"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03] pointer-events-none select-none"
            />
            <div className="absolute inset-0 bg-petrol/0 group-hover:bg-petrol/30 transition-colors duration-300" />
          </motion.button>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-12">
          <button
            onClick={() => setVisibleCount((c) => c + pageSize)}
            className="bg-primary text-primary-foreground rounded-full px-8 py-3.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Charger plus ({images.length - visibleCount} restantes)
          </button>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={visibleImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((i) => (i! - 1 + visibleImages.length) % visibleImages.length)}
          onNext={() => setLightboxIndex((i) => (i! + 1) % visibleImages.length)}
        />
      )}
    </>
  );
};

export default MasonryGallery;
